import React from 'react';
import { cn } from '@/lib/utils';

const TERMINAL_STATUSES = ['completed', 'failed'];

interface TimerProps {
  startTime: string;
  endTime?: string;
  status?: string;
}

export const Timer: React.FC<TimerProps> = ({ startTime, endTime, status }) => {
  const [elapsed, setElapsed] = React.useState<string>('');

  // Timer is finished if we have an endTime OR status is terminal
  const isFinished = !!endTime || (status && TERMINAL_STATUSES.includes(status));

  React.useEffect(() => {
    const calculateDuration = () => {
      const start = new Date(startTime).getTime();
      const end = endTime ? new Date(endTime).getTime() : Date.now();
      const diff = Math.floor((end - start) / 1000);

      if (diff < 60) {
        return `${diff}s`;
      }

      const minutes = Math.floor(diff / 60);
      const seconds = diff % 60;

      if (minutes < 60) {
        return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
      }

      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      return hours > 0 ? `${hours}h ${remainingMinutes}m` : `${minutes}m`;
    };

    setElapsed(calculateDuration());

    if (!isFinished) {
      const timer = setInterval(() => {
        setElapsed(calculateDuration());
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [startTime, endTime, isFinished]);

  const isRunning = !isFinished;

  return (
    <span
      className={cn(
        'text-xs font-mono tabular-nums',
        isRunning ? 'text-blue-600 dark:text-blue-400' : 'text-zinc-400 dark:text-zinc-500'
      )}
    >
      {elapsed}
    </span>
  );
};
